import { createFileRoute } from "@tanstack/react-router";
import { HomePage } from "@/components/home/HomePage";
import { HOME_FAQ_ITEMS, homeFaqAnswerText } from "@/components/home/home-faq";
import "@/styles/home.css";
import { TRIAL_CONTACT_COUNT, TRIAL_CONTACTS_PHRASE } from "@/lib/trial";

const TITLE = "AI Capital Raising Platform & Investor Matching | CapitalDart";
const DESCRIPTION =
  `Find investors, prepare your materials and manage your raise with CapitalDart. Try ${TRIAL_CONTACTS_PHRASE}, outreach drafts and a readiness report free.`;
const OG_TITLE = "CapitalDart | Everything You Need to Raise Capital";
const OG_DESCRIPTION =
  `70,000+ vetted investors. AI matched to your raise. Start with ${TRIAL_CONTACT_COUNT} matched contacts, outreach drafts and an Investor Readiness Report, free.`;
const OG_IMAGE = "https://capitaldart.com/capitaldart-og.png";

const ORGANIZATION_SCHEMA = {
  "@type": "Organization",
  "@id": "https://capitaldart.com/#organization",
  name: "CapitalDart",
  url: "https://capitaldart.com/",
};

const WEBSITE_SCHEMA = {
  "@type": "WebSite",
  "@id": "https://capitaldart.com/#website",
  name: "CapitalDart",
  url: "https://capitaldart.com/",
  publisher: { "@id": "https://capitaldart.com/#organization" },
};

const SOFTWARE_APPLICATION_SCHEMA = {
  "@type": "SoftwareApplication",
  "@id": "https://capitaldart.com/#software",
  name: "CapitalDart",
  url: "https://capitaldart.com/",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  publisher: { "@id": "https://capitaldart.com/#organization" },
  description:
    "An AI capital raising platform for companies, real estate sponsors, fund managers and borrowers. CapitalDart combines investor matching, contact-level research, investor readiness analysis and pipeline tracking. Users can manage the raise themselves with AI-assisted tasks or optionally use AgentDart to execute deal goals and prepare emails for their draft folder or send directly with permission.",
  featureList: [
    "AI investor matching",
    "Contact-level investment criteria",
    "Investor Readiness Report",
    "Personalized outreach drafts",
    "Financial Model Review",
    "Raise Strategy Report",
    "Market Comps and Valuation Report",
    "Indicative Term Sheet",
    "Deal pipeline tracking",
    "AgentDart goal-based capital-raising workflow",
    "Email delivery to draft folder",
    "Direct email sending with permission",
    "30-day plan execution",
    "Warm introduction goals",
    "LinkedIn post strategy goals",
  ],
  offers: {
    "@type": "Offer",
    name: "One-time plan: 500 investor contacts",
    price: "500",
    priceCurrency: "USD",
    url: "https://capitaldart.com/#pricing",
    description:
      "500 investor contacts, chat and AgentDart, and 6 analysis reports (Investor Readiness Report, Financial Model Review, Raise Strategy, Market Comps and Valuation, Indicative Term Sheet); Deal Update Reports are free and unlimited; additional reports 25 credits. No subscription. Two rebates of $250 each are available: one for completing the experience questionnaire, one if CapitalDart uses your comments. Terms are shown on the page.",
  },
};

const HOME_SCHEMA_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_APPLICATION_SCHEMA],
};

const FAQ_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://capitaldart.com/#faq",
  mainEntity: HOME_FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: homeFaqAnswerText(item) },
  })),
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { name: "theme-color", content: "#0d2942" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://capitaldart.com/" },
      { property: "og:title", content: OG_TITLE },
      { property: "og:description", content: OG_DESCRIPTION },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: OG_TITLE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: OG_TITLE },
      { name: "twitter:description", content: OG_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(HOME_SCHEMA_GRAPH) },
      { type: "application/ld+json", children: JSON.stringify(FAQ_PAGE_SCHEMA) },
    ],
  }),
  component: LandingPage,
});


function LandingPage() {
  return <HomePage />;
}
